import { Link } from "react-router-dom";
import { useState } from "react";
import { categories, faqs } from "@/mocks/data";
import FeaturedArticles from "./components/FeaturedArticles";

function FaqItem({ question, answer, open, onToggle }: { question: string; answer: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
      >
        <span className="font-heading text-base text-foreground-900 pr-4">{question}</span>
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i
            className={`text-lg text-foreground-500 transition-transform duration-200 ${
              open ? "ri-subtract-line" : "ri-add-line"
            }`}
          ></i>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20photography%20scene%20with%20soft%20golden%20amber%20and%20rose%20toned%20gradient%20background%2C%20subtle%20lens%20flare%20effects%2C%20elegant%20minimalist%20composition%2C%20dreamy%20atmospheric%20photography%20studio%20vibe%2C%20high%20end%20editorial%20look%2C%20no%20people%20in%20frame&width=1920&height=1080&seq=hero-selfie-2026&orientation=landscape"
            alt="国产自拍完全指南 - 拍出高质感自拍大片"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/35"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6 py-20">
            <div className="max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-5 leading-tight">
                国产自拍完全指南
              </h1>
              <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto">
                国产自拍正在崛起，越来越多人用国产手机和设备拍出媲美专业的自拍作品。汇聚国产自拍技巧、设备推荐、构图方法与后期处理全攻略，助你轻松拍出高质感自拍大片，展现真实自我风采。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/jiqiao"
                  className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  开始学习自拍技巧
                  <div className="w-4 h-4 inline-flex items-center justify-center ml-2">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
                <Link
                  to="/category/shebei"
                  className="px-8 py-3.5 bg-white/15 hover:bg-white/25 text-white font-medium rounded-lg backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  查看设备推荐
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                探索自拍知识分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                从技巧到设备，从构图到后期，六大分类全覆盖你的自拍学习需求
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-background-100/80 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100/80 mb-4 group-hover:bg-accent-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cat.description}
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-sm text-primary-600 font-medium">
                    <span>浏览文章</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <FeaturedArticles />

        {/* FAQ Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于国产自拍，这里有你想要的答案
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <FaqItem
                  key={i}
                  question={faq.question}
                  answer={faq.answer}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20warm%20gradient%20background%20with%20golden%20and%20rose%20tones%2C%20minimalist%20aesthetic%2C%20gentle%20light%20leaks%2C%20elegant%20photography%20studio%20ambient%2C%20clean%20and%20refined%20atmosphere%2C%20high%20key%20editorial%20look&width=1920&height=600&seq=cta-selfie-bottom&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/25"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
              准备好了吗？开始你的自拍进阶之旅
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-8 leading-relaxed">
              加入数以万计的自拍爱好者，一起探索国产自拍的无限可能。从今天开始，每一张自拍都是大片。
            </p>
            <Link
              to="/category/jiqiao"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white hover:bg-background-100 text-foreground-900 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              立即开始学习
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      {/* Structured Data for Homepage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "国产自拍完全指南 - 首页",
            "description": "国产自拍完全指南：汇聚国产自拍技巧、设备推荐、构图方法与后期处理全攻略，助你轻松拍出高质感自拍大片。",
            "url": "https://njaierbei.cn",
            "inLanguage": "zh-CN",
            "isPartOf": {
              "@type": "WebSite",
              "name": "国产自拍完全指南",
              "url": "https://njaierbei.cn",
            },
            "about": {
              "@type": "Organization",
              "name": "国产自拍指南",
              "description": "专注于国产自拍技巧与设备推荐的在线内容平台",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}